<template>
	<div class="transcription-bot-wizard">
		<!-- stage 1: take notes for me -->
		<TeamingAlert
			v-if="stage === stages.PROMPT_START"
			type="SYSTEM"
			variant="HOLLOW"
		>
			<div class="alert-body">
				<p v-html="translate( `stage1Text` )" />
				<div class="input-row">
					<TeamingButton2
						size="TINY"
						@click="stage = 2"
					>
						{{ translate( `takeNotesButton` ) }}
					</TeamingButton2>
					<TeamingButton2
						size="TINY"
						type="TERTIARY"
						@click="$emit( `hide` )"
					>
						{{ translate( `notNowButton` ) }}
					</TeamingButton2>
				</div>
			</div>
		</TeamingAlert>

		<!-- stage 2: edit virtual meeting URL -->
		<TeamingAlert
			v-if="stage === stages.PROMPT_URL"
			type="SYSTEM"
			variant="HOLLOW"
		>
			<div class="alert-body">
				<p>{{ translate( `stage2Text` ) }}</p>
				<div class="input-row">
					<!-- url input -->
					<TeamingTextbox
						:class="`transient-url transient-url__${ subDocId }`"
						name="transientUrl"
						size="SMALL"
						:value="inputValue"
						:placeholder="translate( `stage2Placeholder` )"
						:is-valid="isInputValueValid"
						@input="onUrlInput"
						@paste="onUrlPaste"
					/>
					<TeamingButton2
						size="TINY"
						@click="onSaveClicked"
					>
						{{ needsZoomAuthorization ? translate(`authorizeAndSaveButton`) : translate( `saveButton` ) }}
					</TeamingButton2>
				</div>
				<div>
					<span class="hint">
						{{ translate( `stage2Hint` ) }}
					</span>
				</div>
			</div>
		</TeamingAlert>

		<!-- stage 3: Go! / Edit (back to stage 2) -->
		<TeamingAlert
			v-if="stage === stages.PROMPT_GO"
			type="SYSTEM"
			variant="HOLLOW"
		>
			<div class="alert-body">
				<p>{{ translate( `stage3Text` ) }}</p>
				<div class="input-row">
					<TeamingButton2
						size="TINY"
						type="TERTIARY"
						@click="onEditClicked"
					>
						{{ translate( `editLinkButton` ) }}
					</TeamingButton2>
					<TeamingButton2
						size="TINY"
						@click="onGoClicked"
					>
						{{ translate( `goButton` ) }}
					</TeamingButton2>
				</div>
			</div>
		</TeamingAlert>

		<!-- stage 4: reporting -->

		<!-- bot has been deployed, but is in a retry loop -->
		<TeamingAlert
			v-if="stage === stages.REPORTING && isRetryingAlert"
			class="transcription-status-alert"
			type="SYSTEM"
			variant="HOLLOW"
		>
			<template #icon>
				<FontAwesomeIcon class="bot-icon" :icon="[ `fas`, `message-bot` ]" />
			</template>
			<template #default>
				<div class="alert-body">
					<p>
						{{ translate( `botRetryAlert`, {
							service: virtualMeetingServiceName
						} ) }}
					</p>
					<div>
						<TeamingButton2
							size="TINY"
							type="TERTIARY"
							@click="$emit( `cancelJoin`, { subDocId, } )"
						>{{ translate( `cancelButton` ) }}</TeamingButton2>
					</div>
				</div>
			</template>
		</TeamingAlert>

		<!-- bot has been deployed and is in some state -->
		<TeamingAlert
			v-else-if="stage === stages.REPORTING && isTranscribingAlert && !isRetryingAlert"
			class="transcription-status-alert"
			:type="botAlertConfiguration.alertType"
			:variant="botAlertConfiguration.alertVariant"
		>
			<template #icon v-if="botAlertConfiguration.icon">
				<FontAwesomeIcon class="bot-icon" :icon="botAlertConfiguration.icon" />
			</template>
			<template #default>
				<div class="alert-body">
					<p>
						{{ translate( botAlertConfiguration.translationKey, {
							service: virtualMeetingServiceName
						} ) }}
					</p>
					<div v-if="botAlertConfiguration.cancelable">
						<TeamingButton2
							size="TINY"
							type="TERTIARY"
							@click="$emit( `cancelJoin`, { subDocId, } )"
						>{{ translate( `cancelButton` ) }}</TeamingButton2>
					</div>
				</div>
			</template>
		</TeamingAlert>

		<!-- bot has been deployed has transcription has failed (note the sub-status alert takes precedence here) -->
		<!-- NOTE: this will only be displayed when coming through as an update status; on page load it will not be displayed -->
		<TeamingAlert
			v-else-if="stage === stages.PROMPT_FAIL"
			class="transcription-status-alert"
			type="ERROR"
			variant="HOLLOW"
		>
			<div class="alert-body">
				<p>{{ translate( `botFatalAlert` ) }}</p>
				<div class="input-row">
					<!-- url input -->
					<TeamingTextbox
						:class="`transient-url transient-url__${ subDocId }`"
						name="transientUrl"
						size="SMALL"
						:value="inputValue"
						:placeholder="translate( `stage2Placeholder` )"
						:is-valid="isInputValueValid"
						@input="onUrlInput"
						@paste="onUrlPaste"
					/>
					<TeamingButton2
						size="TINY"
						@click="onGoClicked"
					>
						{{ translate( `goButton` ) }}
					</TeamingButton2>
					<TeamingButton2
						size="TINY"
						type="TERTIARY"
						@click="$emit( `hide` )"
					>
						{{ translate( `notNowButton` ) }}
					</TeamingButton2>
				</div>
			</div>
		</TeamingAlert>

		<div v-else><!-- intentionally empty --></div>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import debugFactory from "debug";

import {
	EventsMixin,
	TranslateMixin,
	TeamingAlert,
	TeamingButton2,
	TeamingTextbox
} from "@teaming/common-ui";

import {
	Enum,
	RecallAIBotStatuses as BotStatus,
	TranscriptionJobStatus,
	TranscriptionOrigin,
	VirtualMeetingType
} from "@teaming/common-enums";

import {
	resolveVirtualMeetingType,
	VirtualMeetingDomains
} from "../../domain";

const debug = debugFactory( "teaming:common-team:components:TranscriptionBotWizard" );

const WizardStage = Enum.create( {
	PROMPT_START: 1,
	PROMPT_URL: 2,
	PROMPT_GO: 3,
	REPORTING: 4,
	PROMPT_FAIL: 5,
} );

const botAlertConfigurations = {
	[ BotStatus.JOINING_CALL ]: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botIsJoiningAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: true,
	},
	[ BotStatus.IN_WAITING_ROOM ]: {
		alertType: "WARN",
		alertVariant: "HOLLOW",
		translationKey: "botIsWaitingAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: true,
	},
	[ BotStatus.IN_CALL_NOT_RECORDING ]: {
		alertType: "SUCCESS",
		alertVariant: "HOLLOW",
		translationKey: "botIsRecordingAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		//
		// TODO: enable once we figure out how to synchronize sub-status delivery better,
		// since IN_CALL_NOT_RECORDING and IN_CALL_RECORDING arrive almost simultaneously
		// and the status sometimes is recorded out-of-order...
		//
		// alertType: "WARN",
		// translationKey: "botIsNotRecordingAlert",
		// icon: [ "fas", "message-bot", ],
	},
	[ BotStatus.IN_CALL_RECORDING ]: {
		alertType: "SUCCESS",
		alertVariant: "HOLLOW",
		translationKey: "botIsRecordingAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
	},
	[ BotStatus.CALL_ENDED ]: {
		alertType: "SUCCESS",
		alertVariant: "HOLLOW",
		translationKey: "botCallEndedAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
	},
	[ BotStatus.DONE ]: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botCallEndedAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
	},
	[ BotStatus.FATAL ]: {
		alertType: "ERROR",
		alertVariant: "HOLLOW",
		translationKey: "botFatalAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
	},
};

export default {
	name: "TranscriptionBotWizard",
	teamingPackage: "common-team",
	mixins: [ EventsMixin, TranslateMixin, ],
	emits: [ "save", "join", "cancelJoin", "hide", ],
	components: {
		TeamingTextbox,
		TeamingAlert,
		TeamingButton2,
	},
	props: {
		subDocId: {
			type: String,
			required: true,
		},
		virtualMeetingUrl: {
			type: String,
			required: true,
		},
		transcriptionOrigin: {
			type: String,
			required: true,
			validator: value => TranscriptionOrigin.includes( value ) || value === "",
		},
		transcriptionStatus: {
			type: String,
			required: true,
			validator: value => TranscriptionJobStatus.includes( value ) || value === "",
		},
		transcriptionSubStatus: {
			type: String,
			required: true,
			validator: value => BotStatus.includes( value ) || value === "",
		},
		// TODO: determine if needed
		// eslint-disable-next-line vue/no-unused-properties
		hasStatusUpdate: {
			type: Boolean,
			required: true,
		},
	},
	watch: {
		stage( newStage ) {
			if ( [ WizardStage.PROMPT_URL, WizardStage.PROMPT_FAIL, ].includes( newStage ) ) {
				this.dirty = false;
			}
		},
	},
	data() {
		return {
			stage: WizardStage.PROMPT_START,
			transientUrl: "",
			dirty: false,
			needsZoomAuthorization: false,
		};
	},
	computed: {
		...mapGetters( "TeamlikeStream", [
			"isZoomAuthorized",
		] ),
		stages() {
			return WizardStage;
		},
		/** this is the source of truth for all computed values, and methods! */
		inputValue() {
			const { dirty, transientUrl, virtualMeetingUrl, } = this;
			return dirty ? transientUrl : virtualMeetingUrl;
		},
		virtualMeetingType() {
			const { inputValue, } = this;
			return resolveVirtualMeetingType( inputValue );
		},
		virtualMeetingServiceName() {
			const { virtualMeetingType, } = this;
			if ( !virtualMeetingType ) {
				return "";
			}
			const translationKey = `${ virtualMeetingType }label`;
			return this.translate( translationKey );
		},
		isInputValueValid() {
			const { dirty, inputValue, virtualMeetingType, } = this;
			if ( !dirty ) {
				return true;
			}
			if ( dirty && !inputValue ) {
				return true;
			}
			return !!( inputValue && virtualMeetingType );
		},
		/** alert configurations based on bot sub-statuses */
		botAlertConfiguration() {
			return botAlertConfigurations[ this.transcriptionSubStatus ] ||
				botAlertConfigurations[ BotStatus.JOINING_CALL ];
		},
		isTranscribingAlert() {
			return [
				TranscriptionJobStatus.REQUESTED,
				TranscriptionJobStatus.REQUESTED_RETRY,
				TranscriptionJobStatus.QUEUED,
				TranscriptionJobStatus.COMMITTED,
				TranscriptionJobStatus.SUMMARIZED,
			].includes( this.transcriptionStatus );
		},
		isRetryingAlert() {
			return this.transcriptionStatus === TranscriptionJobStatus.REQUESTED_RETRY;
		},
	},
	methods: {
		logSelf( lifecycleHook ) {
			const {
				subDocId,
				transcriptionOrigin,
				transcriptionStatus,
				transcriptionSubStatus,
				stage,
			} = this;
			const stageKey = WizardStage.keyFor( stage );
			debug( [
				`[${ lifecycleHook }]`,
				`  subdoc ${ subDocId }`,
				`  stage ${ stageKey }`,
				`  origin ${ transcriptionOrigin }`,
				`  status ${ transcriptionStatus }`,
				`  subStatus ${ transcriptionSubStatus }`,
			].join( "\n" ) );
		},
		checkForZoomLinkAndAuthorization() {
			const { virtualMeetingType, } = this;
			this.needsZoomAuthorization = virtualMeetingType === VirtualMeetingType.ZOOM && !this.isZoomAuthorized;
		},
		onUrlInput( e ) {
			this.transientUrl = e.target.value;
			this.dirty = ( e.target.value !== this.virtualMeetingUrl );
			this.checkForZoomLinkAndAuthorization( e.target.value );
		},
		onUrlPaste( e ) {
			// NOTE: for some reason Google decided to be cute and generate
			// Google Meet URLs without the protocol (e.g., meet.google.com/nkb-uyeu-yxz),
			// so we're automagically prepending it here to help the user out
			const { value, } = e.target;
			const googleMeetDomain = VirtualMeetingDomains[ VirtualMeetingType.GOOGLE_MEET ];
			if ( value.startsWith( googleMeetDomain ) ) {
				this.transientUrl = `https://${ value }`;
				this.dirty = ( this.transientUrl !== this.virtualMeetingUrl );
			}
			this.checkForZoomLinkAndAuthorization( value );
		},
		focusUrlInput() {
			const { subDocId, } = this;
			const e = document.querySelector( `.transient-url__${ subDocId } input` );
			if ( e ) {
				e.focus();
			}
		},
		async onSaveClicked() {
			const {
				inputValue,
				virtualMeetingType,
			} = this;
			let nextStage = WizardStage.PROMPT_URL;
			if ( !inputValue ) {
				nextStage = WizardStage.PROMPT_START;
				this.$emit( "save", { virtualMeetingUrl: "", } );
			}
			if ( virtualMeetingType ) {
				nextStage = WizardStage.PROMPT_GO;
				this.$emit( "save", { virtualMeetingType, virtualMeetingUrl: inputValue, } );
			}
			this.stage = nextStage;
			await this.$nextTick();
			if ( nextStage === WizardStage.PROMPT_URL ) {
				this.focusUrlInput();
			}
		},
		async onEditClicked() {
			this.stage = WizardStage.PROMPT_URL;
			await this.$nextTick();
			this.focusUrlInput();
		},
		async onGoClicked() {
			const { inputValue, } = this;
			this.$emit( "save", { virtualMeetingUrl: inputValue, } );
			await this.$nextTick();
			this.$emit( "join", { virtualMeetingUrl: inputValue, } );
			this.stage = WizardStage.REPORTING;
		},
	},
	mounted() {
		const failStatuses = [ TranscriptionJobStatus.FAILED, TranscriptionJobStatus.ABORTED, ];
		if ( failStatuses.includes( this.transcriptionStatus ) ) {
			this.stage = WizardStage.PROMPT_FAIL;
		} else if ( this.transcriptionStatus ) {
			this.stage = WizardStage.REPORTING;
		} else if ( this.virtualMeetingUrl ) {
			this.stage = WizardStage.PROMPT_GO;
		}
		this.logSelf( "mounted" );
	},
	updated() {
		this.logSelf( "updated" );
	},
};
</script>

<style lang="scss" scoped>
$textColor: $color9-500;

.transcription-bot-wizard {
	color: $textColor;
}

.transcription-bot-wizard .alert-body {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;

	@include anchor() {
		color: $textColor !important;
	}

	& > div {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		gap: 0.5rem;
	}
}

.transcription-bot-wizard .hint {
	@include font-and-line-for(-1);
}

.transcription-bot-wizard .transient-url {
	width: 15.625rem; // 250px
}
</style>
